import React, { useState, useRef, useCallback, useEffect } from 'react';
import type { ApiService } from '@child-nav/shared';
import { AGREEMENT_VERSION, AGREEMENT_TEXT } from '@child-nav/shared';
import {
  sendNewAccountCode,
  createAccountWithCode,
} from '../services/WebAuthService';
import type { SignInResult } from './WebSignInView';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'email' | 'verify' | 'referral';

interface Props {
  apiService: ApiService;
  onSignUp:   (result: SignInResult) => void;
  onBack:     () => void;
}

// ─── Colour tokens (mirrors WebSignInView) ────────────────────────────────────
const C = {
  primary:     '#3498db',
  primaryDark: '#2980b9',
  teal:        '#10b981',
  bg:          '#eef2f7',
  card:        '#ffffff',
  text:        '#1a202c',
  muted:       '#718096',
  border:      '#cbd5e0',
  error:       '#e53e3e',
  errorBg:     '#fff5f5',
  success:     '#38a169',
  successBg:   '#f0fff4',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function darken(hex: string): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, (n >> 16) - 25);
  const g = Math.max(0, ((n >> 8) & 0xff) - 25);
  const b = Math.max(0, (n & 0xff) - 25);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Spinner: React.FC<{ size?: number; color?: string }> = ({ size = 20, color = C.primary }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" fill="none" strokeDasharray="31.4" strokeDashoffset="10" />
  </svg>
);

const Btn: React.FC<{
  label: string;
  onClick: () => void;
  color?: string;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'solid' | 'outline' | 'ghost';
  icon?: string;
}> = ({ label, onClick, color = C.primary, disabled, loading, variant = 'solid', icon }) => {
  const [hov, setHov] = useState(false);
  const base: React.CSSProperties = {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            8,
    padding:        '12px 20px',
    borderRadius:   8,
    fontSize:       15,
    fontWeight:     600,
    cursor:         disabled || loading ? 'not-allowed' : 'pointer',
    border:         variant === 'outline' ? `2px solid ${color}` : 'none',
    background:     variant === 'solid'   ? (hov && !disabled ? darken(color) : color)
                  : variant === 'outline' ? (hov && !disabled ? `${color}15` : 'transparent')
                  : 'transparent',
    color:          variant === 'solid' ? '#fff' : color,
    opacity:        disabled || loading ? 0.6 : 1,
    width:          '100%',
    transition:     'background 0.15s, opacity 0.15s',
    outline:        'none',
    fontFamily:     'inherit',
    boxSizing:      'border-box',
  };
  return (
    <button
      style={base}
      onClick={disabled || loading ? undefined : onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      disabled={disabled || loading}
      type="button"
    >
      {loading && <Spinner size={16} color={variant === 'solid' ? '#fff' : color} />}
      {!loading && icon && <span>{icon}</span>}
      {label}
    </button>
  );
};

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    background:   C.errorBg,
    border:       `1px solid ${C.error}`,
    borderRadius: 8,
    padding:      '10px 14px',
    color:        C.error,
    fontSize:     14,
    marginBottom: 16,
    display:      'flex',
    alignItems:   'flex-start',
    gap:          8,
  }}>
    <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
    <span>{message}</span>
  </div>
);

const SuccessBanner: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    background:   C.successBg,
    border:       `1px solid ${C.success}`,
    borderRadius: 8,
    padding:      '10px 14px',
    color:        C.success,
    fontSize:     14,
    marginBottom: 16,
    display:      'flex',
    alignItems:   'flex-start',
    gap:          8,
  }}>
    <span style={{ fontSize: 16, flexShrink: 0 }}>✅</span>
    <span>{message}</span>
  </div>
);

const LinkBtn: React.FC<{ label: string; onClick: () => void; disabled?: boolean }> = ({ label, onClick, disabled }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    style={{
      background:     'none',
      border:         'none',
      color:          disabled ? C.muted : C.primary,
      fontSize:       13,
      cursor:         disabled ? 'default' : 'pointer',
      padding:        '4px 0',
      textDecoration: 'underline',
      fontFamily:     'inherit',
      opacity:        disabled ? 0.6 : 1,
    }}
  >
    {label}
  </button>
);

const CODE_LENGTH = 6;

const CodeInput: React.FC<{
  value:    string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const refs   = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(CODE_LENGTH, ' ').split('').slice(0, CODE_LENGTH);

  const handleKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = [...digits];
      if (arr[idx].trim() === '' && idx > 0) {
        arr[idx - 1] = ' ';
        onChange(arr.join('').replace(/ +$/, ''));
        refs.current[idx - 1]?.focus();
      } else {
        arr[idx] = ' ';
        onChange(arr.join('').replace(/ +$/, ''));
      }
    } else if (e.key === 'ArrowLeft'  && idx > 0)              refs.current[idx - 1]?.focus();
    else if    (e.key === 'ArrowRight' && idx < CODE_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handleChange = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const arr = [...digits];
    arr[idx] = char;
    onChange(arr.join('').replace(/ +$/, ''));
    if (idx < CODE_LENGTH - 1) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    onChange(pasted);
    refs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
  };

  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
      {Array.from({ length: CODE_LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digits[i].trim()}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          style={{
            width:        48,
            height:       56,
            fontSize:     22,
            fontWeight:   700,
            textAlign:    'center',
            border:       `2px solid ${digits[i].trim() ? C.primary : C.border}`,
            borderRadius: 8,
            outline:      'none',
            background:   '#fff',
            color:        C.text,
            caretColor:   'transparent',
            transition:   'border-color 0.15s',
            fontFamily:   'monospace',
          }}
        />
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const WebSignUpView: React.FC<Props> = ({ apiService, onSignUp, onBack }) => {
  const [step,              setStep]             = useState<Step>('email');
  const [name,              setName]             = useState('');
  const [email,             setEmail]            = useState('');
  const [code,              setCode]             = useState('');
  const [referredBy,        setReferredBy]       = useState('');
  const [error,             setError]            = useState('');
  const [info,              setInfo]             = useState('');
  const [loading,           setLoading]          = useState(false);
  const [resendCool,        setResendCool]       = useState(0);
  const [verificationToken, setVerificationToken] = useState('');
  const [pendingResult,     setPendingResult]    = useState<SignInResult | null>(null);
  const [referralNotFound,  setReferralNotFound] = useState(false);
  const [agreedToTerms,     setAgreedToTerms]    = useState(false);
  const [showAgreement,     setShowAgreement]    = useState(false);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCool <= 0) return;
    const t = setTimeout(() => setResendCool((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCool]);

  const clearMessages = () => { setError(''); setInfo(''); };

  // ── Step 1: send code ──────────────────────────────────────────────────────

  const handleSendCode = useCallback(async () => {
    clearMessages();
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service to continue.');
      return;
    }
    setLoading(true);
    const result = await sendNewAccountCode(email.trim().toLowerCase(), name.trim(), apiService);
    setLoading(false);

    if (result.success && result.verificationToken) {
      setVerificationToken(result.verificationToken);
      setStep('verify');
      setInfo(`A verification code has been sent to ${email}.`);
      setResendCool(60);
    } else {
      setError(result.message ?? 'Could not send the code. Please try again.');
    }
  }, [name, email, agreedToTerms, apiService]);

  // ── Step 1 resend ──────────────────────────────────────────────────────────

  const handleResend = useCallback(async () => {
    clearMessages();
    setLoading(true);
    const result = await sendNewAccountCode(email.trim().toLowerCase(), name.trim(), apiService);
    setLoading(false);

    if (result.success && result.verificationToken) {
      setVerificationToken(result.verificationToken);
      setInfo('A new code has been sent.');
      setResendCool(60);
      setCode('');
    } else {
      setError(result.message ?? 'Could not resend the code.');
    }
  }, [name, email, apiService]);

  // ── Step 2: verify code & create account ──────────────────────────────────

  const handleVerify = useCallback(async () => {
    clearMessages();
    setLoading(true);
    const result = await createAccountWithCode(
      email.trim().toLowerCase(),
      name.trim(),
      code,
      verificationToken,
      apiService,
    );
    setLoading(false);

    if (result.success && result.data) {
      const signInResult: SignInResult = {
        email:             result.data.email,
        token:             result.data.token,
        refreshToken:      result.data.refreshToken,
        name:              result.data.name,
        userId:            result.data.userId,
        loginResponseData: result.data.loginResponseData,
      };
      setPendingResult(signInResult);
      setStep('referral');
    } else {
      setError(result.message ?? 'Could not verify the code. Please try again.');
    }
  }, [email, name, code, verificationToken, apiService]);

  // ── Step 3: referral ──────────────────────────────────────────────────────

  const handleReferralSubmit = useCallback(async () => {
    if (!referredBy.trim()) {
      setReferralNotFound(false);
      setError('Please enter a referral code or email, or tap Skip.');
      return;
    }
    setLoading(true);
    setReferralNotFound(false);
    clearMessages();
    try {
      const userSraId    = pendingResult?.userId ?? '';
      const userSig      = window.localStorage.getItem('user_signature') ?? '';
      const response     = await (apiService as any).accountService({
        RequestType: 'signup_request_referral',
        Payload:     JSON.stringify({
          referredBy:  referredBy.trim(),
          sraId:       userSraId,
          signature:   userSig,
          accesstype:  '5',
        }),
      });
      if (response?.success === true && !response?.errorCode && !response?.errorDescription) {
        if (pendingResult) onSignUp(pendingResult);
      } else {
        setReferralNotFound(true);
      }
    } catch {
      setReferralNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [referredBy, pendingResult, apiService, onSignUp]);

  const handleReferralSkip = useCallback(() => {
    if (pendingResult) onSignUp(pendingResult);
  }, [pendingResult, onSignUp]);

  // ── Input style helper ─────────────────────────────────────────────────────

  const inputStyle: React.CSSProperties = {
    width:        '100%',
    padding:      '11px 14px',
    border:       `2px solid ${C.border}`,
    borderRadius: 8,
    fontSize:     15,
    color:        C.text,
    outline:      'none',
    transition:   'border-color 0.15s',
    fontFamily:   'inherit',
    boxSizing:    'border-box',
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        minHeight:       '100vh',
        background:      C.bg,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '24px 16px',
        fontFamily:      'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{
          background:   C.card,
          borderRadius: 16,
          padding:      '36px 32px',
          width:        '100%',
          maxWidth:     420,
          boxShadow:    '0 4px 24px rgba(0,0,0,0.10)',
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>
              Child Nav
            </h1>
            <p style={{ margin: '6px 0 0', color: C.muted, fontSize: 14 }}>
              {step === 'email'    && 'Create a new family account'}
              {step === 'verify'   && 'Check your email'}
              {step === 'referral' && 'Were you referred?'}
            </p>
          </div>

          {/* Banners */}
          {error && <ErrorBanner   message={error} />}
          {info  && <SuccessBanner message={info}  />}

          {/* ── email step ───────────────────────────────────────────────── */}
          {step === 'email' && (
            <>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.muted }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearMessages(); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                  placeholder="you@example.com"
                  autoFocus
                  autoComplete="email"
                  disabled={loading}
                  style={inputStyle}
                  onFocus={(e) => { e.currentTarget.style.borderColor = C.primary; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = C.border;  }}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.muted }}>
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => { setName(e.target.value); clearMessages(); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                  placeholder="e.g. Mom, Dad, Nurse Sarah"
                  autoComplete="name"
                  disabled={loading}
                  style={inputStyle}
                  onFocus={(e) => { e.currentTarget.style.borderColor = C.primary; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = C.border;  }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20 }}>
                <input
                  type="checkbox"
                  id="terms-agree"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  disabled={loading}
                  style={{ marginTop: 2, width: 16, height: 16, accentColor: C.primary, flexShrink: 0, cursor: loading ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="terms-agree" style={{ fontSize: 13, color: C.muted, lineHeight: '1.5', cursor: loading ? 'not-allowed' : 'pointer' }}>
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowAgreement(true)}
                    style={{ background: 'none', border: 'none', padding: 0, color: C.primary, textDecoration: 'underline', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}
                  >
                    Terms of Service &amp; Privacy Policy
                  </button>
                </label>
              </div>

              <Btn
                label="Send Verification Code"
                onClick={handleSendCode}
                color={C.primary}
                loading={loading}
                disabled={!name.trim() || !email.trim() || !agreedToTerms}
                icon="✉️"
              />

              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <span style={{ fontSize: 13, color: C.muted }}>Already have an account? </span>
                <LinkBtn label="Sign in" onClick={onBack} disabled={loading} />
              </div>
            </>
          )}

          {/* ── verify step ──────────────────────────────────────────────── */}
          {step === 'verify' && (
            <>
              <p style={{ textAlign: 'center', fontSize: 14, color: C.muted, marginBottom: 20 }}>
                Enter the 6-digit code we sent to<br />
                <strong style={{ color: C.text }}>{email}</strong>
              </p>

              <CodeInput value={code} onChange={setCode} disabled={loading} />

              <p style={{ textAlign: 'center', fontSize: 12, color: C.muted, marginBottom: 20, marginTop: 4 }}>
                The code expires in 10 minutes.
              </p>

              <Btn
                label="Verify & Create Account"
                onClick={handleVerify}
                color={C.primary}
                loading={loading}
                disabled={code.length < CODE_LENGTH}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <LinkBtn
                  label="← Change email"
                  onClick={() => { setStep('email'); setCode(''); setVerificationToken(''); clearMessages(); }}
                  disabled={loading}
                />
                <LinkBtn
                  label={resendCool > 0 ? `Resend code (${resendCool}s)` : 'Resend code'}
                  onClick={handleResend}
                  disabled={loading || resendCool > 0}
                />
              </div>
            </>
          )}

          {/* ── referral step ─────────────────────────────────────────────── */}
          {step === 'referral' && (
            <>
              <p style={{ fontSize: 14, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
                Help the person who referred you earn Gift Rewards! Enter their email or account code below, or skip to continue.
              </p>

              <div style={{ marginBottom: 8 }}>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: C.muted }}>
                  Referred by (optional)
                </label>
                <input
                  type="text"
                  value={referredBy}
                  onChange={(e) => { setReferredBy(e.target.value); setReferralNotFound(false); clearMessages(); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleReferralSubmit()}
                  placeholder="Referring email or account code"
                  autoCapitalize="none"
                  autoCorrect="off"
                  disabled={loading}
                  style={inputStyle}
                  onFocus={(e) => { e.currentTarget.style.borderColor = C.primary; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = C.border;  }}
                />
              </div>

              {referralNotFound && (
                <div style={{
                  background:   '#fffbeb',
                  border:       '1px solid #f59e0b',
                  borderRadius: 8,
                  padding:      '10px 14px',
                  color:        '#92400e',
                  fontSize:     13,
                  marginBottom: 16,
                }}>
                  ⚠️ That code or email was not found. Please check and try again, or skip to continue without a referral.
                </div>
              )}

              <div style={{ marginBottom: 8 }}>
                <Btn
                  label="Submit"
                  onClick={handleReferralSubmit}
                  color={C.primary}
                  loading={loading}
                  disabled={!referredBy.trim()}
                />
              </div>
              <Btn
                label="Skip"
                onClick={handleReferralSkip}
                color={C.muted}
                variant="ghost"
                loading={false}
                disabled={loading}
              />
            </>
          )}
        </div>
      </div>

      {/* ── Terms of Service Overlay ─────────────────────────────────── */}
      {showAgreement && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Terms of Service"
          style={{
            position:        'fixed',
            inset:           0,
            background:      'rgba(0,0,0,0.55)',
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            zIndex:          1000,
            padding:         '24px 16px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAgreement(false); }}
        >
          <div style={{
            background:   '#ffffff',
            borderRadius: 16,
            maxWidth:     560,
            width:        '100%',
            maxHeight:    '80vh',
            display:      'flex',
            flexDirection:'column',
            boxShadow:    '0 8px 40px rgba(0,0,0,0.25)',
          }}>
            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17, color: C.text }}>Terms of Service &amp; Privacy Policy</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Version {AGREEMENT_VERSION}</div>
              </div>
              <button
                type="button"
                onClick={() => setShowAgreement(false)}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 20, color: C.muted, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
              >
                ✕
              </button>
            </div>
            {/* body */}
            <div style={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
              <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: 13, lineHeight: '1.7', color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {AGREEMENT_TEXT}
              </pre>
            </div>
            {/* footer */}
            <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
              <Btn label="Close" onClick={() => setShowAgreement(false)} color={C.primary} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
